import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useNewRelic } from '@e3dc-react/shell/Libs/observability-tool'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { Box, Button, useTheme } from '@mui/material'
import React from 'react'
import { useTypedSelector } from 'src/State/RootReducer'
import { useUserSettings } from 'src/Hooks'
import useModals from 'src/Hooks/useModals'
import { Ai360PowerFixLogo } from '../../../resources'
import { ExtensionCategoryNames, ExtensionDetailsProps, ExtensionState, ExtensionTypes } from '../../../types'
import { DetailsViewWrapper, DisconnectButton, HeaderContent } from '../../general'
import { useOpenInexogyModal } from '../../../hooks/inexogy'
import useInstallationExtensionData from '../../../hooks/extensions/use-installation-extension-data'

/**
 * Gives a detailed description of the ai360 power fix extension.
 */
const Ai360PowerFixDetails: React.FC<ExtensionDetailsProps> = ({ extensionState, hasActiveTariff, isLoading, onClose, place = 0 }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const openInexogyModal = useOpenInexogyModal()
  const contractStateProps = {
    contractState: extensionState,
    isLoading,
  }

  return (
    <DetailsViewWrapper
      extensionType={ExtensionTypes.Ai360PowerFix}
      descriptions={
        extensionState === 'active'
          ? [t('installation-extensions.ai360-power-fix.details.active-description')]
          : [t('installation-extensions.ai360-power-fix.details.description')]
      }
      headerContent={<HeaderContent logo={<Ai360PowerFixLogo />} />}
      footerContent={<FooterContent hasActiveTariff={hasActiveTariff} extensionState={extensionState} />}
      onClose={onClose}
      place={place}
      open={true}
      category={{ name: ExtensionCategoryNames.ProsumerTariff, paletteColor: theme.palette.primary, variant: 'light' }}
      contractStateProps={contractStateProps}
      linkHref="#"
      onLinkClick={openInexogyModal}
    />
  )
}

interface FooterContentProps {
  hasActiveTariff?: boolean
  extensionState?: ExtensionState
}

const FooterContent: React.FC<FooterContentProps> = ({ hasActiveTariff, extensionState }) => {
  const { t } = useCustomTranslation()
  const { userSettings } = useUserSettings()
  const { pageActions } = useNewRelic()
  const { openAi360SmartMeterRecommendationModal, openAi360PowerFixDeleteConnectionModal } = useModals()
  const installationId = useTypedSelector(state => state.storage.activeInstallation)?.toString() ?? ''
  const { loadInstallationExtensionStates } = useInstallationExtensionData(installationId)
  const isConnected = extensionState === 'active'

  const onClick = (): void => {
    pageActions.componentClicked({
      componentName: 'Ai360PowerFixDetailsDialog',
      userId: userSettings.userid?.toString(),
    })
    openAi360SmartMeterRecommendationModal({
      tariffType: ExtensionTypes.Ai360PowerFix,
      onContinue: () => {
        window.open('https://www.e3dc.com/power/fix/', '_blank', 'noopener,noreferrer')
      },
    })
  }

  const handleDisconnectExtension = (): void => {
    openAi360PowerFixDeleteConnectionModal({ loadInstallationExtensionStates })
  }

  if (isConnected) return <DisconnectButton isConnected={isConnected} onClickLink={handleDisconnectExtension} />

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <OptionalTooltipWrapper hasTooltip={hasActiveTariff} titleKey={'installation-extensions.ai360-power-fix.connection-button.disabled-tooltip'}>
        <Button variant="outlined" onClick={onClick} disabled={hasActiveTariff}>
          {t('installation-extensions.ai360-power-fix.modal.button')}
        </Button>
      </OptionalTooltipWrapper>
    </Box>
  )
}

export default Ai360PowerFixDetails
